import { Box, InputCheckBox, Text } from '@factorialco/deprecated-design-system'
import { F0Dialog } from '@factorialco/f0-react'
import { FormikProps } from 'formik'
import { Features } from 'generated/featureDefinitions'
import {
  CreateAttendanceMessageDocument,
  CreateAttendanceMessageMutationVariables,
} from 'generated/resources/graphql'

import { observer } from 'mobx-react'

import TextArea from 'modules/core/components/TextArea'
import { useForm } from 'modules/core/hooks/useForm'
import { useMutation } from 'modules/core/hooks/useMutation'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import { useAttendanceFeatures } from 'modules/attendance/lib/attendanceFeaturesContext'

interface SendEmailReminderFormProps {
  message: string
  sendCopy: boolean
}

interface Props {
  employeeId: string
  employeeFullName: string
  selectedEmployeeIds?: string[]
  onClose: () => void
}

const SendEmailReminder = ({
  employeeId,
  employeeFullName,
  selectedEmployeeIds,
  onClose,
}: Props) => {
  const { featureAvailability: hasRealTimeAttendance } = useAttendanceFeatures(
    Features.time_tracking_real_time_attendance
  )
  let employeeIds = [employeeId]
  let title = i18n.t('dashboard.who_is_in.reminder_modal.title')
  let successMessage = i18n.t('dashboard.who_is_in.reminder_modal.reminder_sent', {
    userName: employeeFullName,
  })
  let sendCopyText = i18n.t('dashboard.who_is_in.reminder_modal.send_copy')
  const isTimesheetPage = hasRealTimeAttendance && !!selectedEmployeeIds

  if (isTimesheetPage) {
    employeeIds = selectedEmployeeIds
    title = i18n.t('dashboard.who_is_in.reminder_modal_bulk.title')
    successMessage = i18n.t('dashboard.who_is_in.reminder_modal_bulk.reminder_sent')
    sendCopyText = i18n.t('dashboard.who_is_in.reminder_modal_bulk.send_copy')
  }

  const [createAttendanceMessage, {}] = useMutation(CreateAttendanceMessageDocument, {
    extractorFn: (data) => data.attendanceMutations.createAttendanceMessage,
  })

  const create = (variables: CreateAttendanceMessageMutationVariables) => {
    return createAttendanceMessage({
      variables,
    })
  }

  const form: FormikProps<SendEmailReminderFormProps> = useForm<SendEmailReminderFormProps>({
    defaultValues: {
      message: '',
      sendCopy: false,
    },
    onSubmit: async (data, { setErrors }) => {
      const variables = {
        employeeIds,
        message: data.message,
        sendCopy: data.sendCopy,
      }

      const result = await create(variables)

      let errorMessage = null
      const handled = handleResult(result, (errorsHash) => {
        errorMessage = errorsHash.messages
        return setErrors(errorsHash)
      })

      if (handled.success) {
        app.setFlash({
          type: 'correct',
          message: successMessage,
        })
      } else if (errorMessage) {
        app.setFlash({
          type: 'wrong',
          message: errorMessage,
        })
      }
      form.resetForm()

      return handled
    },
  })

  const sendReminder = () => {
    track(trackingEvents.timeTracking.sendClockInReminderSubmit, {
      from: isTimesheetPage ? 'page' : 'widget',
      user_count: employeeIds.length,
    })
    form.handleSubmit()
    onClose()
  }

  return (
    <F0Dialog
      isOpen
      onClose={onClose}
      title={title}
      primaryAction={{
        label: i18n.t('dashboard.who_is_in.reminder_modal.send_reminder'),
        onClick: sendReminder,
      }}
      secondaryAction={{
        label: i18n.t('common.cancel'),
        onClick: onClose,
      }}
    >
      <Box flexDirection="column" gap="s2" paddingX="s24" paddingY="s16">
        <TextArea
          value={form.values.message}
          placeholder={i18n.t('dashboard.who_is_in.reminder_modal.placeholder')}
          onChange={(message) => form.setFieldValue('message', message)}
        />
        <Box flexDirection="row" alignItems="center" gap="s8" paddingTop="s24">
          <InputCheckBox
            onClick={() => form.setFieldValue('sendCopy', !form.values.sendCopy)}
            checked={form.values.sendCopy}
          />
          <Text size="200">{sendCopyText}</Text>
        </Box>
      </Box>
    </F0Dialog>
  )
}

export default observer(SendEmailReminder)
